import Typography from "components/basic/typography";
import { useTranslation } from "react-i18next";
import ContactInformationImage from "resources/images/ContactInformationImage.png";
import { useNavigate } from "react-router-dom";
import TextField from "components/basic/textField";
import { useSnack } from "hooks/useSnack";
import { useMaskedInput } from "hooks/useMaskedInput";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import { formatPhoneNumber, PHONE_NUMBER_PLACEHOLDER } from "pages/formTools";
import FormPageLayout from "components/basic/formPageLayout";

const ContactInformation: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("estimator");
  const handlePhoneChange = useMaskedInput(formatPhoneNumber);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, "");
    // Check if we have exactly 10 digits
    return digitsOnly.length === 10;
  };

  const handleReceiveEstimate = () => {
    if (!chargerEstimatorData.fullName?.trim()) {
      showSnack(t("contact.name_required"), "error");
      return;
    }
    if (
      !chargerEstimatorData.email?.trim() ||
      !validateEmail(chargerEstimatorData.email)
    ) {
      showSnack(t("contact.email_invalid"), "error");
      return;
    }
    if (
      !chargerEstimatorData.phoneNumber?.trim() ||
      !validatePhone(chargerEstimatorData.phoneNumber)
    ) {
      showSnack(t("contact.phone_invalid"), "error");
      return;
    }
    navigate("?currentPage=reviewDetails");
  };

  return (
    <FormPageLayout
      imageSrc={ContactInformationImage}
      imageAlt={t("contact.image_alt")}
      imagePosition="right"
      contentTextAlign="right"
      title={
        <Typography
          style={{ fontWeight: "bold", fontSize: "2.5em" }}
          variant="h2"
          align="inherit"
        >
          {t("contact.title")}
        </Typography>
      }
      backAction={{
        label: t("nav.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=preferences"),
      }}
      primaryAction={{
        label: "Review Details",
        color: "warning",
        onClick: handleReceiveEstimate,
      }}
    >
      <div>
        <Typography variant="body2" align="inherit">
          {t("contact.full_name")}
        </Typography>

        <TextField
          placeHolder="Please Enter"
          name="fullName"
          size="tiny"
          textAlign="right"
          value={chargerEstimatorData.fullName || ""}
          onChange={({ target: { value } }) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              fullName: value,
            })
          }
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("contact.email")}
        </Typography>

        <TextField
          placeHolder="Please Enter"
          textAlign="right"
          name="email"
          size="tiny"
          value={chargerEstimatorData.email || ""}
          onChange={({ target: { value } }) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              email: value,
            })
          }
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("contact.phone")}
        </Typography>

        <TextField
          placeHolder={PHONE_NUMBER_PLACEHOLDER}
          textAlign="right"
          name="phoneNumber"
          size="tiny"
          value={chargerEstimatorData.phoneNumber || ""}
          onChange={(event) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              phoneNumber: handlePhoneChange(
                event,
                chargerEstimatorData.phoneNumber || "",
              ),
            })
          }
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("contact.additional")}
        </Typography>

        <TextField
          placeHolder="Enter (If Applicable)"
          textAlign="right"
          size="tiny"
          name="additionalInformation"
          rows={4}
          value={chargerEstimatorData.additionalInformation || ""}
          onChange={({ target: { value } }) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              additionalInformation: value,
            })
          }
        />
      </div>
    </FormPageLayout>
  );
};

export default ContactInformation;
